import { useMemo, useState, useEffect, useRef } from 'react';
import { Comment, HoldRequestComment, LineItemComment } from '@/types';
import { formatDistanceToNow } from 'date-fns';

interface CommentHistoryProps {
comments: Comment[] | HoldRequestComment[] | LineItemComment[];
loading?: boolean;
error?: string | null;
emptyMessage?: string;
showPagination?: boolean;
currentPage?: number;
totalPages?: number;
totalCount?: number;
onPageChange?: (page: number) => void;
pageSize?: number;
enableCollapsible?: boolean;
initialCollapsedCount?: number;
lastViewedTimestamp?: Date | null;
recentThresholdMinutes?: number;
onViewedTimestampUpdate?: (timestamp: Date) => void;
}

function getActionContextLabel(context?: string): string {
const labels: Record<string, string> = {
status_change: 'Status Change',
assignment: 'Assignment',
approval: 'Approval',
rejection: 'Rejection',
expiration_update: 'Expiration Update',
bulk_update: 'Bulk Update',
migration: 'Migration',
cancellation: 'Cancellation',
reopening: 'Reopening',
completion: 'Completion',
open: 'Open',
in_progress: 'In Progress',
completed: 'Completed',
exception_required: 'Exception Required',
revision_required: 'Revision Required',
};
return context ? labels[context] || context : '';
}

function getActionContextColor(context?: string): string {
const colors: Record<string, string> = {
status_change: 'bg-outfront-purple-light text-outfront-purple',
assignment: 'bg-purple-100 text-purple-800',
approval: 'bg-green-100 text-green-800',
rejection: 'bg-red-100 text-red-800',
expiration_update: 'bg-yellow-100 text-yellow-800',
bulk_update: 'bg-indigo-100 text-indigo-800',
migration: 'bg-neutral-100 text-neutral-900',
cancellation: 'bg-red-100 text-red-800',
reopening: 'bg-outfront-purple-light text-outfront-purple',
completion: 'bg-green-100 text-green-800',
open: 'bg-outfront-purple-light text-outfront-purple',
in_progress: 'bg-yellow-100 text-yellow-800',
completed: 'bg-green-100 text-green-800',
exception_required: 'bg-purple-100 text-purple-800',
revision_required: 'bg-red-100 text-red-800',
};
return context ? colors[context] || 'bg-neutral-100 text-neutral-900' : '';
}

interface CommentItemProps {
comment: Comment;
isRecent?: boolean;
isUnread?: boolean;
}

function CommentItem({ comment, isRecent = false, isUnread = false }: CommentItemProps) {
const commentRef = useRef<HTMLDivElement>(null);

const timeAgo = useMemo(() => {
try {
return formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true });
} catch {
return 'Unknown time';
}
}, [comment.createdAt]);

const isSystemComment = comment.isSystemGenerated;

// Determine border and background styling based on state
const getBorderAndBackgroundClasses = () => {
if (isUnread) {
return isSystemComment
? 'bg-outfront-purple-light border-outfront-purple border-l-4'
: 'bg-outfront-purple-light border-outfront-purple border-l-4';
}
if (isRecent) {
return isSystemComment
? 'bg-yellow-50 border-yellow-300 border-l-4'
: 'bg-yellow-50 border-yellow-400 border-l-4';
}
return isSystemComment
? 'bg-neutral-50 border-neutral-200'
: 'bg-white border-neutral-200';
};

return (
<div
ref={commentRef}
data-testid={`comment-${comment.id}`}
data-comment-timestamp={new Date(comment.createdAt).getTime()}
data-comment-id={comment.id}
className={`p-4 rounded-lg border transition-all duration-300 ${getBorderAndBackgroundClasses()}`}
>
{/* Unread/Recent indicator badges */}
{(isUnread || isRecent) && (
<div className="flex items-center gap-2 mb-2">
{isUnread && (
<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-outfront-purple text-white">
<svg
className="w-3 h-3 mr-1"
fill="currentColor"
viewBox="0 0 20 20"
>
<path
fillRule="evenodd"
d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
clipRule="evenodd"
/>
</svg>
New
</span>
)}
{isRecent && !isUnread && (
<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600 text-white">
<svg
className="w-3 h-3 mr-1"
fill="currentColor"
viewBox="0 0 20 20"
>
<path
fillRule="evenodd"
d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
clipRule="evenodd"
/>
</svg>
Recent
</span>
)}
</div>
)}

<div className="flex items-start justify-between gap-3 mb-2">
<div className="flex items-center gap-2 flex-1 min-w-0">
<div className="flex-shrink-0">
{isSystemComment ? (
<div className="w-8 h-8 rounded-full bg-neutral-200 flex items-center justify-center">
<svg
className="w-4 h-4 text-neutral-600"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
/>
</svg>
</div>
) : (
<div className="w-8 h-8 rounded-full bg-outfront-purple flex items-center justify-center text-white text-sm font-medium">
{comment.authorName.charAt(0).toUpperCase()}
</div>
)}
</div>

<div className="flex-1 min-w-0">
<div className="flex items-center gap-2 flex-wrap">
<span className="font-medium text-neutral-900 truncate">
{isSystemComment ? 'System' : comment.authorName}
</span>
{comment.actionContext && (
<span
className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionContextColor(
comment.actionContext
)}`}
>
{getActionContextLabel(comment.actionContext)}
</span>
)}
</div>
<span className="text-sm text-neutral-600">{timeAgo}</span>
</div>
</div>
</div>

<div className="ml-10">
<p className="text-neutral-600 whitespace-pre-wrap break-words">
{comment.commentText}
</p>

{comment.actionDetails && Object.keys(comment.actionDetails).length > 0 && (
<div className="mt-2 text-xs text-neutral-600 bg-neutral-100 rounded p-2">
<details>
<summary className="cursor-pointer font-medium">Action Details</summary>
<pre className="mt-1 overflow-x-auto">
{JSON.stringify(comment.actionDetails, null, 2)}
</pre>
</details>
</div>
)}
</div>
</div>
);
}

export function CommentHistory({
comments,
loading = false,
error = null,
emptyMessage = 'No comments yet',
showPagination = false,
currentPage = 1,
totalPages = 1,
totalCount = 0,
onPageChange,
pageSize = 20,
enableCollapsible = false,
initialCollapsedCount = 5,
lastViewedTimestamp = null,
recentThresholdMinutes = 5,
onViewedTimestampUpdate,
}: CommentHistoryProps) {
const [isExpanded, setIsExpanded] = useState(false);
const containerRef = useRef<HTMLDivElement>(null);
const [scrollPosition, setScrollPosition] = useState<number>(0);
const previousCommentsLength = useRef<number>(comments.length);

// Update last viewed timestamp when component mounts or when user views comments
useEffect(() => {
if (comments.length > 0 && onViewedTimestampUpdate) {
// Update timestamp after a short delay to allow user to see the comments
const timer = setTimeout(() => {
onViewedTimestampUpdate(new Date());
}, 2000);
return () => clearTimeout(timer);
}
return undefined;
}, [comments.length, onViewedTimestampUpdate]);

// Maintain scroll position when new comments are added
useEffect(() => {
if (containerRef.current && comments.length > previousCommentsLength.current) {
// New comments were added, maintain scroll position
const container = containerRef.current;
const scrollableParent = container.closest('[data-scrollable]') || window;

if (scrollableParent === window) {
window.scrollTo({ top: scrollPosition, behavior: 'auto' });
} else if (scrollableParent instanceof HTMLElement) {
scrollableParent.scrollTop = scrollPosition;
}
}
previousCommentsLength.current = comments.length;
}, [comments.length, scrollPosition]);

// Save scroll position before updates
useEffect(() => {
const handleScroll = () => {
if (containerRef.current) {
const container = containerRef.current;
const scrollableParent = container.closest('[data-scrollable]');

if (scrollableParent instanceof HTMLElement) {
setScrollPosition(scrollableParent.scrollTop);
} else {
setScrollPosition(window.scrollY);
}
}
};

const scrollableParent = containerRef.current?.closest('[data-scrollable]');

if (scrollableParent instanceof HTMLElement) {
scrollableParent.addEventListener('scroll', handleScroll);
return () => {
scrollableParent.removeEventListener('scroll', handleScroll);
};
} else {
window.addEventListener('scroll', handleScroll);
return () => {
window.removeEventListener('scroll', handleScroll);
};
}
}, []);

// Sort comments reverse chronologically (newest first)
const sortedComments = useMemo(() => {
return [...comments].sort((a, b) => {
return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
});
}, [comments]);

// Determine which comments are recent or unread
const commentsWithStatus = useMemo(() => {
const now = new Date();
const recentThreshold = new Date(now.getTime() - recentThresholdMinutes * 60 * 1000);
const lastViewed = lastViewedTimestamp ? new Date(lastViewedTimestamp) : null;

return sortedComments.map((comment) => {
const commentDate = new Date(comment.createdAt);
const isRecent = commentDate > recentThreshold;
const isUnread = lastViewed ? commentDate > lastViewed : false;

return {
comment,
isRecent,
isUnread,
};
});
}, [sortedComments, recentThresholdMinutes, lastViewedTimestamp]);

// Determine which comments to display based on collapsible state
const displayedComments = useMemo(() => {
if (!enableCollapsible || isExpanded || commentsWithStatus.length <= initialCollapsedCount) {
return commentsWithStatus;
}
return commentsWithStatus.slice(0, initialCollapsedCount);
}, [commentsWithStatus, enableCollapsible, isExpanded, initialCollapsedCount]);

const hiddenCount = commentsWithStatus.length - displayedComments.length;
const unreadCount = commentsWithStatus.filter((c) => c.isUnread).length;
const recentCount = commentsWithStatus.filter((c) => c.isRecent && !c.isUnread).length;
const shouldShowCollapsibleButton = enableCollapsible && commentsWithStatus.length > initialCollapsedCount;

if (loading) {
return (
<div className="flex items-center justify-center py-8">
<div className="flex flex-col items-center gap-2">
<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-outfront-purple"></div>
<p className="text-sm text-neutral-600">Loading comments...</p>
</div>
</div>
);
}

if (error) {
return (
<div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-4 py-3">
<div className="flex items-start gap-2">
<svg
className="w-5 h-5 flex-shrink-0 mt-0.5"
fill="currentColor"
viewBox="0 0 20 20"
>
<path
fillRule="evenodd"
d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
clipRule="evenodd"
/>
</svg>
<div>
<p className="font-medium">Error loading comments</p>
<p className="mt-1">{error}</p>
</div>
</div>
</div>
);
}

if (sortedComments.length === 0) {
return (
<div className="text-center py-8 text-neutral-600">
<svg
className="mx-auto h-12 w-12 text-neutral-400"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
/>
</svg>
<p className="mt-2">{emptyMessage}</p>
</div>
);
}

return (
<div ref={containerRef} className="space-y-4">
{/* Comment count and info */}
{totalCount > 0 && (
<div className="flex items-center justify-between text-sm text-neutral-600 pb-2 border-b border-neutral-200">
<div className="flex items-center gap-3">
<span>
{totalCount} {totalCount === 1 ? 'comment' : 'comments'}
</span>
{unreadCount > 0 && (
<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-outfront-purple text-white">
{unreadCount} new
</span>
)}
{recentCount > 0 && (
<span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-600 text-white">
{recentCount} recent
</span>
)}
</div>
{showPagination && totalPages > 1 && (
<span>
Page {currentPage} of {totalPages}
</span>
)}
</div>
)}

<div className="space-y-3">
{displayedComments.map(({ comment, isRecent, isUnread }) => (
<CommentItem
key={comment.id}
comment={comment}
isRecent={isRecent}
isUnread={isUnread}
/>
))}
</div>

{/* Collapsible expand/collapse button */}
{shouldShowCollapsibleButton && (
<button
onClick={() => setIsExpanded(!isExpanded)}
className="w-full py-2 px-4 text-sm font-medium text-outfront-purple hover:text-outfront-purple hover:bg-outfront-purple-light rounded-md border border-outfront-purple transition-colors"
>
{isExpanded ? (
<span className="flex items-center justify-center gap-2">
<svg
className="w-4 h-4"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M5 15l7-7 7 7"
/>
</svg>
Show fewer comments
</span>
) : (
<span className="flex items-center justify-center gap-2">
<svg
className="w-4 h-4"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M19 9l-7 7-7-7"
/>
</svg>
Show {hiddenCount} more {hiddenCount === 1 ? 'comment' : 'comments'}
</span>
)}
</button>
)}

{/* Pagination controls */}
{showPagination && totalPages > 1 && onPageChange && (
<div className="flex items-center justify-between border-t border-neutral-200 pt-4">
<button
onClick={() => onPageChange(currentPage - 1)}
disabled={currentPage === 1}
className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
>
<svg
className="w-4 h-4"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M15 19l-7-7 7-7"
/>
</svg>
Previous
</button>

<div className="flex items-center gap-2">
<span className="text-sm text-neutral-600">
Page {currentPage} of {totalPages}
</span>
{totalCount > 0 && (
<span className="text-xs text-neutral-600">
({(currentPage - 1) * pageSize + 1}-
{Math.min(currentPage * pageSize, totalCount)} of {totalCount})
</span>
)}
</div>

<button
onClick={() => onPageChange(currentPage + 1)}
disabled={currentPage === totalPages}
className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-neutral-600 bg-white border border-neutral-200 rounded-md hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white transition-colors"
>
Next
<svg
className="w-4 h-4"
fill="none"
stroke="currentColor"
viewBox="0 0 24 24"
>
<path
strokeLinecap="round"
strokeLinejoin="round"
strokeWidth={2}
d="M9 5l7 7-7 7"
/>
</svg>
</button>
</div>
)}
</div>
);
}
